import {
  useEffect,
  useState
} from 'react'

import {
  motion,
  AnimatePresence
} from 'framer-motion'

import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
  FaAward
} from 'react-icons/fa'

import {
  AdminCard,
  Field,
  Input,
  BtnPrimary,
  BtnOutline,
  ColorPicker
} from './AdminUI'

import {
  getCertificates,
  addCertificate,
  updateCertificate,
  deleteCertificate
} from '../../../api/portfolioApi'

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const CERT_ICONS = [
  '⚛️',
  '🟢',
  '☕',
  '🐍',
  '🐙',
  '🎨',
  '🏆',
  '📜',
  '💡',
  '🔒',
  '🌐',
  '☁️',
  '🤖',
  '📱',
  '🎯'
]

const emptyForm = {
  title: '',
  issuer: '',
  date: '',
  credentialId: '',
  color: 'blue',
  icon: '🏆'
}

// ─────────────────────────────────────────────
// Certificate Row
// ─────────────────────────────────────────────
function CertRow({
  cert,
  onEdit,
  onDelete
}) {

  const [confirmDel, setConfirmDel] =
    useState(false)

  const colorDot = {
    blue: '#3B82F6',
    green: '#10B981',
    orange: '#F59E0B',
    yellow: '#EAB308',
    purple: '#8B5CF6',
    cyan: '#06B6D4'
  }

  return (

    <motion.div

      layout

      initial={{
        opacity: 0,
        y: 10
      }}

      animate={{
        opacity: 1,
        y: 0
      }}

      exit={{
        opacity: 0,
        x: -20
      }}

      className="
        flex
        items-center
        gap-4
        p-4
        rounded-xl
        bg-white/3
        border
        border-white/5
      "
    >

      {/* Icon */}
      <span
        className="
          text-2xl
          w-8
          text-center
          flex-shrink-0
        "
      >
        {cert.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">

        <div className="flex items-center gap-2">

          <span
            className="
              w-2
              h-2
              rounded-full
              flex-shrink-0
            "

            style={{
              background:
                colorDot[cert.color]
                || colorDot.blue
            }}
          />

          <p
            className="
              font-medium
              text-sm
              text-white
              truncate
            "
          >
            {cert.title}
          </p>

        </div>

        <p
          className="
            text-xs
            text-slate-500
            mt-0.5
            font-mono
          "
        >
          {cert.issuer}
          {' · '}
          {cert.date}
        </p>

      </div>

      {/* Actions */}
      <div
        className="
          flex
          items-center
          gap-2
          flex-shrink-0
        "
      >

        {confirmDel ? (

          <>

            <span
              className="
                text-xs
                text-red-400
                font-mono
              "
            >
              Delete?
            </span>

            <motion.button

              onClick={() =>
                onDelete(cert.id)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-red-500/10
                border
                border-red-500/20
                flex
                items-center
                justify-center
                text-red-400
              "
            >

              <FaCheck size={10} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(false)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-400
              "
            >

              <FaTimes size={10} />

            </motion.button>

          </>

        ) : (

          <>

            <motion.button

              onClick={() =>
                onEdit(cert)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-blue-500/10
                border
                border-blue-500/20
                flex
                items-center
                justify-center
                text-blue-400
              "
            >

              <FaEdit size={10} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(true)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-500
              "
            >

              <FaTrash size={10} />

            </motion.button>

          </>
        )}

      </div>

    </motion.div>
  )
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function CertificatesEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // State
  // ───────────────────────────────────────────
  const [certificates, setCertificates] =
    useState([])

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [form, setForm] =
    useState(emptyForm)

  const [editingId, setEditingId] =
    useState(null)

  const [formOpen, setFormOpen] =
    useState(false)

  // ───────────────────────────────────────────
  // Load Certificates
  // ───────────────────────────────────────────
  useEffect(() => {
    loadCertificates()
  }, [])

  const loadCertificates =
    async () => {

      try {

        setLoading(true)

        const data =
          await getCertificates()

        setCertificates(
          data || []
        )

      } catch (err) {

        console.error(err)

        setError(
          'Failed to load certificates'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Update Field
  // ───────────────────────────────────────────
  const updateField = (
    key,
    value
  ) => {

    setForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // ───────────────────────────────────────────
  // Add
  // ───────────────────────────────────────────
  const openAdd = () => {

    setForm(emptyForm)

    setEditingId(null)

    setFormOpen(true)
  }

  // ───────────────────────────────────────────
  // Edit
  // ───────────────────────────────────────────
  const openEdit = (cert) => {

    setForm({

      title:
        cert.title ?? '',

      issuer:
        cert.issuer ?? '',

      date:
        cert.date ?? '',

      credentialId:
        cert.credentialId ?? '',

      color:
        cert.color ?? 'blue',

      icon:
        cert.icon ?? '🏆'
    })

    setEditingId(cert.id)

    setFormOpen(true)
  }

  // ───────────────────────────────────────────
  // Save
  // ───────────────────────────────────────────
  const handleSave = async () => {

    if (
      !form.title.trim()
      || !form.issuer.trim()
      || !form.date.trim()
    ) {
      return
    }

    try {

      setLoading(true)

      setError('')

      // Update
      if (editingId) {

        await updateCertificate(
          editingId,
          form
        )

        setCertificates(prev =>
          prev.map(cert =>
            cert.id === editingId
              ? {
                  ...cert,
                  ...form
                }
              : cert
          )
        )

        onSaved?.(
          'Certificate updated!'
        )

      }

      // Add
      else {

        const created =
          await addCertificate(form)

        setCertificates(prev => [
          ...prev,
          created.certificate
        ])

        onSaved?.(
          'Certificate added!'
        )
      }

      // Reset
      setForm(emptyForm)

      setEditingId(null)

      setFormOpen(false)

    } catch (err) {

      console.error(err)

      setError(
        err.message ||
        'Failed to save certificate'
      )

    } finally {

      setLoading(false)
    }
  }

  // ───────────────────────────────────────────
  // Delete
  // ───────────────────────────────────────────
  const handleDelete = async (id) => {

    try {

      setLoading(true)

      await deleteCertificate(id)

      setCertificates(prev =>
        prev.filter(
          cert => cert.id !== id
        )
      )

      onSaved?.(
        'Certificate deleted.'
      )

    } catch (err) {

      console.error(err)

      setError(
        err.message ||
        'Failed to delete certificate'
      )

    } finally {

      setLoading(false)
    }
  }

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────
  return (

    <AdminCard

      title="Certificates"

      subtitle={`
        ${certificates.length}
        certificates
      `}

      action={
        <BtnPrimary
          onClick={openAdd}
          small
        >

          <FaPlus size={10} />

          Add Certificate

        </BtnPrimary>
      }
    >

      {/* Error */}
      {error && (

        <div
          className="
            mb-4
            rounded-xl
            border
            border-red-500/20
            bg-red-500/10
            px-4
            py-3
            text-sm
            text-red-400
          "
        >
          {error}
        </div>
      )}

      {/* Form */}
      <AnimatePresence>

        {formOpen && (

          <motion.div

            initial={{
              opacity: 0,
              height: 0
            }}

            animate={{
              opacity: 1,
              height: 'auto'
            }}

            exit={{
              opacity: 0,
              height: 0
            }}

            className="
              overflow-hidden
              mb-6
            "
          >

            <div
              className="
                p-5
                rounded-xl
                border
                border-blue-500/20
                bg-blue-500/5
                mb-4
              "
            >

              <h4
                className="
                  font-display
                  font-semibold
                  text-sm
                  text-white
                  mb-4
                  flex
                  items-center
                  gap-2
                "
              >

                <FaAward
                  size={12}
                  className="text-blue-400"
                />

                {editingId
                  ? 'Edit Certificate'
                  : 'Add New Certificate'}

              </h4>

              {/* Icon Picker */}
              <div className="mb-4">

                <label
                  className="
                    block
                    font-mono
                    text-xs
                    text-slate-500
                    mb-2
                    tracking-widest
                  "
                >
                  ICON
                </label>

                <div
                  className="
                    flex
                    flex-wrap
                    gap-2
                  "
                >

                  {CERT_ICONS.map(icon => (

                    <button

                      key={icon}

                      type="button"

                      onClick={() =>
                        updateField(
                          'icon',
                          icon
                        )
                      }

                      className={`
                        w-9
                        h-9
                        rounded-lg
                        text-lg
                        flex
                        items-center
                        justify-center
                        transition-all

                        ${
                          form.icon === icon

                          ? `
                            bg-blue-500/20
                            border-2
                            border-blue-500/50
                            scale-110
                          `

                          : `
                            bg-white/5
                            border
                            border-white/10
                          `
                        }
                      `}
                    >

                      {icon}

                    </button>
                  ))}

                </div>

              </div>

              {/* Inputs */}
              <div
                className="
                  grid
                  grid-cols-1
                  sm:grid-cols-2
                  gap-4
                "
              >

                <Field
                  label="
                    Certificate Title *
                  "
                >

                  <Input
                    value={form.title}

                    onChange={(e) =>
                      updateField(
                        'title',
                        e.target.value
                      )
                    }

                    maxLength={100}
                  />

                </Field>

                <Field
                  label="
                    Issuing Organization *
                  "
                >

                  <Input
                    value={form.issuer}

                    onChange={(e) =>
                      updateField(
                        'issuer',
                        e.target.value
                      )
                    }

                    maxLength={80}
                  />

                </Field>

                <Field label="Date *">

                  <Input
                    type="date"
                    value={form.date}
                    required

                    onChange={(e) =>
                      updateField(
                        'date',
                        e.target.value
                      )
                    }

                    maxLength={50}
                  />

                </Field>

                <Field label="Credential ID">

                  <Input
                    value={
                      form.credentialId
                    }

                    onChange={(e) =>
                      updateField(
                        'credentialId',
                        e.target.value
                      )
                    }

                    maxLength={80}
                  />

                </Field>

              </div>

              {/* Color */}
              <Field label="Accent Color">

                <ColorPicker
                  value={form.color}

                  onChange={(value) =>
                    updateField(
                      'color',
                      value
                    )
                  }
                />

              </Field>

              {/* Buttons */}
              <div
                className="
                  flex
                  gap-3
                  mt-2
                "
              >

                <BtnPrimary
                  onClick={handleSave}
                >

                  <FaCheck size={11} />

                  {editingId
                    ? 'Update'
                    : 'Add Certificate'}

                </BtnPrimary>

                <BtnOutline

                  onClick={() => {

                    setFormOpen(false)

                    setEditingId(null)

                    setForm(emptyForm)
                  }}
                >

                  <FaTimes size={11} />

                  Cancel

                </BtnOutline>

              </div>

            </div>

          </motion.div>
        )}

      </AnimatePresence>

      {/* List */}
      <div className="space-y-2">

        <AnimatePresence>

          {certificates.length === 0 ? (

            <div
              className="
                text-center
                py-10
                text-slate-600
                font-mono
                text-sm
              "
            >
              No certificates yet.
            </div>

          ) : (

            certificates.map(cert => (

              <CertRow

                key={cert.id}

                cert={cert}

                onEdit={openEdit}

                onDelete={handleDelete}

              />
            ))
          )}

        </AnimatePresence>

      </div>

    </AdminCard>
  )
}